import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppStore } from "../../../appStore";
import debounce from "lodash/debounce";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useFormik } from "formik";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { api_url } from "../../../apiUtils";
import { toast } from "react-toastify";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import DownloadIcon from "@mui/icons-material/Download";
import { saveAs } from "file-saver";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { compressImageFiles } from "../../../utils/compressImageFile";

function AddPoints() {
  const {
    setAddPointOpen,
    depositSent,
    setDepositSent,
    profileData,
    preferredDepositWalletType,
    setPreferredDepositWalletType,
  } = useAppStore();
  const navigate = useNavigate();
  const [files, setFiles] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isOtherPayment, setIsOtherPayment] = useState(false); // State to track if "Other" is selected
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scannerImage, setScannerImage] = useState(null);
  const [scannerImageName, setScannerImageName] = useState(null);
  const [scannerImageId, setScannerImageId] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [scannerFetchFailed, setScannerFetchFailed] = useState(false);
  const [minimumDepositAmount, setMinimumDepositAmount] = useState(null);
  const [mdrTaxDeduction, setMdrTaxDeduction] = useState(0);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [scannerPreviewOpen, setScannerPreviewOpen] = useState(false);
  const [walletType, setWalletType] = useState("main");
  const canUseAutoGameWallet = Boolean(profileData?.canUseAutoGameWallet);

  const setsnackbarMessage = useAppStore((state) => state.setSnackbarMessage);
  const setopenSnackbar = useAppStore((state) => state.setOpenSnackbar);

  const validationSchema = yup.object().shape({
    requestedAmount: yup
      .number()
      .required("Points is required")
      .min(
        minimumDepositAmount,
        `Minimum amount should be ${minimumDepositAmount} and above.`,
      ),
    paymentMode: yup.string().required("Payment Mode is required"),
    customPaymentMode: yup.string(),
  });

  const handleFileChange = async (event) => {
    const inputFiles = event.target.files;
    const compressedFiles = await compressImageFiles(inputFiles);
    setFiles(compressedFiles);

    // Create image preview URLs
    const filePreviews = compressedFiles.map((file) =>
      URL.createObjectURL(file),
    );
    setImagePreview(filePreviews);
  };

  // wallet settings

  const getWalletSettings = () => {
    let token = sessionStorage.getItem("accesstoken");
    axios
      .get(`${api_url}wallet/get-common-wallet-settings`, {
        headers: {
          accesstoken: token,
        },
      })
      .then((response) => {
        let data = response.data;
        console.log("data", data);
        if (data.status === "success") {
          setMinimumDepositAmount(
            data.getWalletSettings[0].minimumDepositAmount,
          );
          setMdrTaxDeduction(
            Number(data.getWalletSettings[0].mdrTaxDeduction || 0),
          );
          setLoadingSettings(false);
        }
      });
  };

  useEffect(() => {
    getWalletSettings();
  }, []);

  useEffect(() => {
    if (!canUseAutoGameWallet && walletType !== "main") {
      setWalletType("main");
    }
  }, [canUseAutoGameWallet, walletType]);

  useEffect(() => {
    if (
      canUseAutoGameWallet &&
      ["main", "autoGame"].includes(preferredDepositWalletType)
    ) {
      setWalletType(preferredDepositWalletType);
    }
  }, [canUseAutoGameWallet, preferredDepositWalletType]);

  const formik = useFormik({
    initialValues: {
      requestedAmount: "",
      paymentMode: "",
      customPaymentMode: "", // Initial value for custom payment mode
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (isSubmitting) return; // Prevent multiple clicks

      setIsSubmitting(true); // Disable button after first click

      const formData = new FormData();
      formData.append("requestedAmount", values.requestedAmount);
      formData.append(
        "paymentMode",
        isOtherPayment ? values.customPaymentMode : values.paymentMode, // Send custom payment mode if "Other" is selected
      );
      if (!files) {
        toast.error("Please upload the payment screenshot.");
        setIsSubmitting(false);
        return;
      }

      // Append files to formData
      if (files) {
        Array.from(files).forEach((file) => {
          formData.append("image", file);
        });
      }

      try {
        const token = sessionStorage.getItem("accesstoken");
        const response = await axios.post(
          walletType === "autoGame"
            ? `${api_url}wallet/auto-game/deposit`
            : `${api_url}wallet/deposit`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              accesstoken: token,
            },
          },
        );

        const data = response.data;
        if (data.status === "success") {
          setDepositSent(true);
          toast.success("Request processing..");
          navigate("/wallet/manage-points");
          setAddPointOpen(false);
          setPreferredDepositWalletType("main");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred while sending the request",
        );
      }

      setIsSubmitting(false); // Re-enable button after request completes
      // Reset the form fields after successful submission
      resetForm();
    },
  });

  const handlePaymentModeChange = (event) => {
    const inputPaymentMode = event.target.value;
    formik.setFieldValue("paymentMode", inputPaymentMode);
    formik.setFieldTouched("paymentMode", true);

    // Check if "Other" is selected and update state
    if (inputPaymentMode === "other") {
      setIsOtherPayment(true);
    } else {
      setIsOtherPayment(false);
    }
  };

  const handleRequestedAmountChange = (event) => {
    const inputRequestedAmount = event.target.value.replace(/\D/g, "");
    formik.setFieldValue("requestedAmount", inputRequestedAmount);
  };

  const handleCustomPaymentModeChange = (event) => {
    const inputCustomPaymentMode = event.target.value;
    formik.setFieldValue("customPaymentMode", inputCustomPaymentMode);
  };

  const minForScanner = minimumDepositAmount ?? 300;
  const needsScanner = Number(formik.values.requestedAmount) >= minForScanner;
  const mdrPreview = useMemo(() => {
    const amount = Number(formik.values.requestedAmount || 0);
    const percent = Number(mdrTaxDeduction || 0);
    const mdrAmount =
      walletType === "main" && amount > 1999 && percent > 0
        ? (amount * percent) / 100
        : 0;

    return {
      isApplied: mdrAmount > 0,
      mdrAmount: mdrAmount.toFixed(2),
      netAmount: (amount - mdrAmount).toFixed(2),
      percent,
    };
  }, [formik.values.requestedAmount, mdrTaxDeduction, walletType]);

  const fetchRandomScanner = useCallback(async () => {
    setScannerLoading(true);
    setScannerFetchFailed(false);
    setScannerImage((currentImage) => {
      if (currentImage?.startsWith("blob:")) {
        URL.revokeObjectURL(currentImage);
      }
      return null;
    });
    setScannerImageId(null);
    setShowScanner(false);
    try {
      const token = sessionStorage.getItem("accesstoken");
      const response = await axios.get(
        `${api_url}wallet/get-random-scanner?t=${Date.now()}`,
        {
          params:
            walletType === "autoGame" && canUseAutoGameWallet
              ? { walletType: "autoGame" }
              : {},
          headers: { accesstoken: token },
        },
      );
      const scanner = response?.data?.image;
      if (scanner?._id) {
        const imageResponse = await axios.get(
          `${api_url}wallet/scanner-file/${scanner._id}`,
          {
            headers: { accesstoken: token },
            responseType: "blob",
          },
        );
        setScannerImage(URL.createObjectURL(imageResponse.data));
        setScannerImageName(scanner.originalname);
        setScannerImageId(scanner._id);
        setShowScanner(true);
      } else {
        setScannerFetchFailed(true);
      }
    } catch (error) {
      setScannerFetchFailed(true);
      toast.error("Payment scanner could not be loaded. Please try again.");
    } finally {
      setScannerLoading(false);
    }
  }, [canUseAutoGameWallet, walletType]);

  const DEBOUNCE_SCANNER_MS = 500;
  const debouncedFetchScanner = useMemo(
    () => debounce(fetchRandomScanner, DEBOUNCE_SCANNER_MS),
    [fetchRandomScanner],
  );

  useEffect(() => {
    if (needsScanner) {
      setScannerFetchFailed(false);
      debouncedFetchScanner();
    } else {
      debouncedFetchScanner.cancel();
      setShowScanner(false);
      setScannerImage((currentImage) => {
        if (currentImage?.startsWith("blob:")) {
          URL.revokeObjectURL(currentImage);
        }
        return null;
      });
      setScannerImageId(null);
      setScannerLoading(false);
      setScannerFetchFailed(false);
    }
    return () => debouncedFetchScanner.cancel();
  }, [formik.values.requestedAmount, needsScanner, debouncedFetchScanner]);

  return (
    <Box
      sx={{
        maxHeight: { xs: "calc(100dvh - 56px)", sm: "calc(100vh - 108px)" },
        overflowY: "auto",
        pr: { xs: 0.25, sm: 0.75 },

        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.3)",
        },
        "&::-webkit-scrollbar-button": {
          display: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: { xs: 1.25, sm: 2.5 },
          p: { xs: 1.25, sm: 2.5 },
          borderRadius: { xs: 2, sm: 3 },
          color: "#fff",
          background: "linear-gradient(135deg, #171b4d 0%, #303b91 100%)",
          boxShadow: "0 12px 28px rgba(23, 27, 77, 0.22)",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 120,
            height: 120,
            borderRadius: "50%",
            top: -65,
            right: -35,
            background: "rgba(255, 216, 10, 0.14)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          <Box
            sx={{
              width: { xs: 36, sm: 46 },
              height: { xs: 36, sm: 46 },
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: 2.25,
              color: "#171b4d",
              background: "#FFD80A",
            }}
          >
            <AccountBalanceWalletRoundedIcon
              sx={{ fontSize: { xs: 21, sm: 24 } }}
            />
          </Box>
          <Box>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1rem", sm: "1.4rem" },
                lineHeight: 1.2,
                fontWeight: 800,
              }}
            >
              Add Points to Wallet
            </Typography>
            <Typography
              sx={{
                mt: 0.2,
                color: "rgba(255,255,255,0.7)",
                fontSize: { xs: "0.68rem", sm: "0.78rem" },
              }}
            >
              Send a secure deposit request
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: { xs: 1, sm: 2 },
            px: { xs: 0.8, sm: 1.25 },
            py: { xs: 0.6, sm: 0.9 },
            borderRadius: 2,
            background: "rgba(255,255,255,0.1)",
          }}
        >
          <AccessTimeRoundedIcon
            sx={{ color: "#FFD80A", fontSize: { xs: 17, sm: 20 } }}
          />
          <Typography
            sx={{ fontSize: { xs: "0.66rem", sm: "0.78rem" }, lineHeight: 1.4 }}
          >
            Verification hours: <strong>10:15 AM – 11:30 PM</strong>
            <br />
            Points are usually updated within <strong>10 minutes</strong>.
          </Typography>
        </Box>
      </Box>

      <Box height={{ xs: 2, sm: 10 }} />
      {canUseAutoGameWallet && (
        <Grid container spacing={1} sx={{ mb: { xs: 1.5, sm: 2.5 } }}>
          <Grid item xs={12}>
            <Typography
              sx={{
                color: "#171b4d",
                fontWeight: 700,
                fontSize: { xs: "0.76rem", sm: "0.86rem" },
              }}
            >
              Wallet
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1,
              }}
            >
              {[
                { label: "Main Wallet", value: "main" },
                { label: "Auto Game Wallet", value: "autoGame" },
              ].map((option) => {
                const selected = walletType === option.value;
                return (
                  <Box
                    key={option.value}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={() => setWalletType(option.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setWalletType(option.value);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      minHeight: 46,
                      px: 1.25,
                      borderRadius: 2.25,
                      border: "1.5px solid",
                      borderColor: selected ? "#303b91" : "#d9deea",
                      backgroundColor: selected ? "#eef1ff" : "#f8f9fc",
                      color: "#171b4d",
                      cursor: "pointer",
                      transition: "all 160ms ease",
                    }}
                  >
                    <Radio
                      checked={selected}
                      size="small"
                      value={option.value}
                      name="walletType"
                      onChange={() => setWalletType(option.value)}
                      sx={{
                        p: 0,
                        color: "#8a92ad",
                        "&.Mui-checked": { color: "#303b91" },
                      }}
                    />
                    <Typography sx={{ fontSize: "0.86rem", fontWeight: 700 }}>
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      )}
      {formik.touched.requestedAmount && formik.errors.requestedAmount && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            py: 1.25,
            px: 2,
            mb: 1.5,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "#d32f2f",
            bgcolor: "rgba(211, 47, 47, 0.08)",
            color: "#b71c1c",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
            fontWeight: 500,
            boxShadow: "0 2px 8px rgba(211, 47, 47, 0.2)",
          }}
        >
          <Box component="span" sx={{ fontSize: "1.25rem", lineHeight: 1 }}>
            ⚠
          </Box>
          <Box component="span">{formik.errors.requestedAmount}</Box>
        </Box>
      )}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography
            sx={{
              color: "#171b4d",
              fontWeight: 700,
              fontSize: { xs: "0.76rem", sm: "0.86rem" },
            }}
          >
            Deposit points
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="requestedAmount"
            name="requestedAmount"
            type="tel"
            fullWidth
            placeholder="Enter points"
            value={formik.values.requestedAmount}
            onChange={handleRequestedAmountChange}
            onBlur={formik.handleBlur}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              enterKeyHint: "done",
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
                backgroundColor: "#f8f9fc",
              },
              "& .MuiOutlinedInput-input": {
                py: { xs: 1.15, sm: 2 },
                px: { xs: 1.25, sm: 1.75 },
                fontSize: { xs: "0.82rem", sm: "1rem" },
              },
            }}
          />
          {mdrPreview.isApplied && (
            <Box
              sx={{
                mt: 1,
                p: { xs: 1, sm: 1.25 },
                border: "1px solid #ffb74d",
                borderRadius: 2,
                bgcolor: "#fff8e1",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.72rem", sm: "0.82rem" },
                  fontWeight: 800,
                  color: "#8a4b00",
                }}
              >
                MDR Tax Deduction will be applied
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: { xs: "0.68rem", sm: "0.78rem" },
                  color: "#6b5b35",
                }}
              >
                {mdrPreview.percent}% MDR deducts {mdrPreview.mdrAmount} points.
                Net wallet credit: {mdrPreview.netAmount} points.
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
      {needsScanner && !files && (
        <Grid container mt={3} justifyContent="center">
          <Grid item xs={12} textAlign="center">
            {scannerLoading || (!scannerImage && !scannerFetchFailed) ? (
              <Box
                sx={{
                  minHeight: 180,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  border: "1px dashed #ccc",
                  borderRadius: 2,
                  py: 3,
                }}
              >
                <CircularProgress size={40} sx={{ color: "#1e2466" }} />
                <Typography variant="subtitle1" color="text.secondary">
                  Loading payment scanner...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Please wait before submitting
                </Typography>
              </Box>
            ) : scannerImage ? (
              <>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Click On QR And Save In Gallery
                </Typography>
                <Box
                  component="img"
                  src={scannerImage}
                  alt="Scanner QR"
                  onClick={() => setScannerPreviewOpen(true)}
                  sx={{
                    width: showScanner ? 150 : 200,
                    height: showScanner ? 150 : 200,
                    opacity: showScanner ? 1 : 0,
                    cursor: "pointer",
                    transition: "all 0.5s ease-in-out",
                    transform: showScanner ? "scale(1)" : "scale(0.8)",
                    border: "1px solid #ccc",
                    borderRadius: 2,
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  minHeight: 180,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  border: "1px dashed #ccc",
                  borderRadius: 2,
                  py: 3,
                }}
              >
                <Typography variant="body2" color="error">
                  Payment scanner could not be loaded.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setScannerFetchFailed(false);
                    fetchRandomScanner();
                  }}
                  disabled={scannerLoading}
                >
                  Try again
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      {/* Payment Mode */}
      <Grid container mt={{ xs: 1.5, sm: 2.5 }}>
        <Grid item xs={12}>
          <Typography
            sx={{
              color: "#171b4d",
              fontWeight: 700,
              mb: { xs: 0.6, sm: 1 },
              fontSize: { xs: "0.76rem", sm: "0.86rem" },
            }}
          >
            Select payment mode
          </Typography>
        </Grid>

        {/* ICON ROW WITH RADIO LEFT OF ICON */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            {/* PhonePe */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.35, sm: 0.75 },
                minHeight: { xs: 60, sm: 78 },
                p: { xs: 0.5, sm: 1 },
                borderRadius: 2.5,
                border: "1.5px solid",
                borderColor:
                  formik.values.paymentMode === "ppe" ? "#5f259f" : "#e1e4ec",
                backgroundColor:
                  formik.values.paymentMode === "ppe" ? "#f7f0ff" : "#fff",
                cursor: "pointer",
                transition: "all 160ms ease",
              }}
              onClick={() => {
                formik.setFieldValue("paymentMode", "ppe");
                setIsOtherPayment(false);
              }}
            >
              <input
                type="radio"
                name="paymentMode"
                style={{ display: "none" }}
                checked={formik.values.paymentMode === "ppe"}
                onChange={() => {
                  formik.setFieldValue("paymentMode", "ppe");
                  setIsOtherPayment(false);
                }}
              />
              <img src="/images/p-pay1.png" alt="PhonePe" width="32px" />
              <Typography
                sx={{
                  fontSize: { xs: "0.62rem", sm: "0.72rem" },
                  fontWeight: 700,
                }}
              >
                PhonePe
              </Typography>
            </Box>

            {/* GPay */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.35, sm: 0.75 },
                minHeight: { xs: 60, sm: 78 },
                p: { xs: 0.5, sm: 1 },
                borderRadius: 2.5,
                border: "1.5px solid",
                borderColor:
                  formik.values.paymentMode === "gpay" ? "#4285f4" : "#e1e4ec",
                backgroundColor:
                  formik.values.paymentMode === "gpay" ? "#eef5ff" : "#fff",
                cursor: "pointer",
                transition: "all 160ms ease",
              }}
              onClick={() => {
                formik.setFieldValue("paymentMode", "gpay");
                setIsOtherPayment(false);
              }}
            >
              <input
                type="radio"
                name="paymentMode"
                style={{ display: "none" }}
                checked={formik.values.paymentMode === "gpay"}
                onChange={() => {
                  formik.setFieldValue("paymentMode", "gpay");
                  setIsOtherPayment(false);
                }}
              />
              <img src="/images/g-pay1.png" alt="GPay" width="32px" />
              <Typography
                sx={{
                  fontSize: { xs: "0.62rem", sm: "0.72rem" },
                  fontWeight: 700,
                }}
              >
                Google Pay
              </Typography>
            </Box>

            {/* Paytm */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.35, sm: 0.75 },
                minHeight: { xs: 60, sm: 78 },
                p: { xs: 0.5, sm: 1 },
                borderRadius: 2.5,
                border: "1.5px solid",
                borderColor:
                  formik.values.paymentMode === "paytm" ? "#00baf2" : "#e1e4ec",
                backgroundColor:
                  formik.values.paymentMode === "paytm" ? "#edfaff" : "#fff",
                cursor: "pointer",
                transition: "all 160ms ease",
              }}
              onClick={() => {
                formik.setFieldValue("paymentMode", "paytm");
                setIsOtherPayment(false);
              }}
            >
              <input
                type="radio"
                name="paymentMode"
                style={{ display: "none" }}
                checked={formik.values.paymentMode === "paytm"}
                onChange={() => {
                  formik.setFieldValue("paymentMode", "paytm");
                  setIsOtherPayment(false);
                }}
              />
              <img src="/images/paytm.png" alt="Paytm" width="32px" />
              <Typography
                sx={{
                  fontSize: { xs: "0.62rem", sm: "0.72rem" },
                  fontWeight: 700,
                }}
              >
                Paytm
              </Typography>
            </Box>

            {/* OTHER OPTION (commented as you requested) */}
            {/*
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
        }}
        onClick={() => {
          formik.setFieldValue("paymentMode", "other");
          setIsOtherPayment(true);
        }}
      >
        <input
          type="radio"
          name="paymentMode"
          checked={formik.values.paymentMode === "other"}
          onChange={() => {
            formik.setFieldValue("paymentMode", "other");
            setIsOtherPayment(true);
          }}
        />
        <KeyboardIcon sx={{ width: 30 }} />
      </Box>
      */}
          </Box>

          {/* ERROR */}
          {formik.touched.paymentMode && formik.errors.paymentMode && (
            <Typography color="error" fontSize={12} mt={1}>
              {formik.errors.paymentMode}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Conditionally render the custom payment mode input field */}
      {isOtherPayment && (
        <Grid container mt={3}>
          <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
            <Typography>Enter Payment Mode :</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              id="customPaymentMode"
              name="customPaymentMode"
              value={formik.values.customPaymentMode}
              onChange={handleCustomPaymentModeChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        </Grid>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: { xs: 1.5, sm: 2.5 },
          mb: { xs: 0.75, sm: 1 },
          color: "#171b4d",
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18, color: "#168451" }} />
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
          Upload payment receipt
        </Typography>
      </Box>

      <Grid container item xs={12}>
        {imagePreview ? (
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: { xs: 1, sm: 2 },
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                background: "#f3f4f8",
                width: "100%",
                maxWidth: 400,
                height: { xs: 145, sm: 210 },
                borderRadius: { xs: 2, sm: 3 },
                border: "1px solid #e0e3ec",
              }}
            >
              {imagePreview.map((img, index) => (
                <img
                  width={"100%"}
                  height={"100%"}
                  key={index}
                  src={img}
                  alt={`Uploaded ${index}`}
                />
              ))}
            </Box>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Button
              variant="outlined"
              component="label"
              sx={{
                width: "100%",
                border: "1.5px dashed #9aa2bd",
                borderRadius: { xs: 2, sm: 3 },
                px: { xs: 1, sm: 0 },
                py: { xs: 1.25, sm: 2.5 },
                minHeight: { xs: 102, sm: 135 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.5, sm: 1 },
                color: "#303b91",
                backgroundColor: "#f8f9fd",
                textTransform: "none",
                transition: "all 160ms ease",
                "&:hover": {
                  borderColor: "#303b91",
                  backgroundColor: "#f1f3fb",
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 34, sm: 44 },
                  height: { xs: 34, sm: 44 },
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  color: "#fff",
                  background: "linear-gradient(135deg, #171b4d, #3c49a5)",
                }}
              >
                <CloudUploadIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </Box>

              <Typography
                variant="body1"
                sx={{
                  px: 0.5,
                  fontWeight: 700,
                  fontSize: { xs: "0.76rem", sm: "1rem" },
                  lineHeight: { xs: 1.25, sm: 1.5 },
                  textAlign: "center",
                  overflowWrap: "anywhere",
                }}
              >
                Upload Transaction Successful Screenshot
              </Typography>
              <Typography
                sx={{
                  color: "#858b9d",
                  fontSize: { xs: "0.62rem", sm: "0.7rem" },
                  textAlign: "center",
                }}
              >
                JPG, PNG or image from your gallery
              </Typography>

              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                hidden
              />
            </Button>
          </Grid>
        )}
      </Grid>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: { xs: 0.75, sm: 1.25 },
          mt: { xs: 1.5, sm: 2.5 },
          pb: 0.25,
        }}
      >
        <Box sx={{ flex: "0 0 34%" }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={() => {
              setPreferredDepositWalletType("main");
              setAddPointOpen(false);
            }}
            sx={{
              minHeight: { xs: 40, sm: 46 },
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Cancel
          </Button>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Button
            fullWidth
            sx={{
              minHeight: { xs: 40, sm: 46 },
              borderRadius: 2.5,
              textTransform: "none",
              background: "linear-gradient(135deg, #171b4d, #303b91)",
              letterSpacing: 0.5,
              fontWeight: 800,
              boxShadow: "0 8px 20px rgba(23,27,77,0.2)",
            }}
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={
              isSubmitting ||
              (needsScanner && !files && (scannerLoading || !scannerImage))
            }
          >
            {isSubmitting
              ? "Verifying..."
              : needsScanner && !files && (scannerLoading || !scannerImage)
                ? "Loading scanner..."
                : "Verify & Add Points"}
          </Button>
        </Box>
      </Box>
      <Modal
        open={scannerPreviewOpen}
        onClose={() => setScannerPreviewOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "calc(100% - 32px)",
            maxWidth: 360,
            maxHeight: "calc(100dvh - 32px)",
            overflowY: "auto",
            bgcolor: "#fff",
            borderRadius: 4,
            p: { xs: 2, sm: 2.5 },
            boxShadow: "0 24px 70px rgba(10,16,45,0.35)",
            textAlign: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            Scan & Pay
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            {scannerImageName}
          </Typography>
          <Box
            component="img"
            src={scannerImage}
            alt="Scanner QR Full"
            sx={{
              width: "100%",
              borderRadius: 2,
              mb: 2,
            }}
          />

          <Button
            variant="contained"
            fullWidth
            sx={{ backgroundColor: "green" }}
            onClick={async () => {
              try {
                const token = sessionStorage.getItem("accesstoken");

                const response = await axios.get(
                  `${api_url}wallet/scanner-file/${scannerImageId}`,
                  {
                    params: { download: 1 },
                    headers: { accesstoken: token },
                    responseType: "blob",
                  },
                );
                saveAs(response.data, scannerImageName);
              } catch (err) {
                toast.error("Scanner download failed. Please try again.");
              }
            }}
          >
            <DownloadIcon />
            Save & Send Money
          </Button>
          <Button
            onClick={() => setScannerPreviewOpen(false)}
            fullWidth
            sx={{ mt: 1 }}
            variant="outlined"
            color="error"
          >
            Close
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}

export default AddPoints;
